/**
 * Heuristiques de detection PWA / add-to-home-screen.
 *
 * Sur iPad Safari, il n'existe PAS d'API d'install prompt (contrairement
 * a Chrome/Edge desktop qui exposent `beforeinstallprompt`). L'utilisateur
 * doit passer par le menu Partage > « Sur l'ecran d'accueil ». On se
 * contente donc de detecter le contexte iPad Safari et d'afficher un
 * banniere subtile qui guide ce geste.
 *
 * Une fois l'app installee, `window.navigator.standalone` (propriete
 * non-standard iOS) est `true`. On l'utilise pour masquer la banniere
 * en mode app installee.
 */
const DISMISS_KEY = "gsf:pwa-install-dismissed:v1";
// Re-proposer apres 30 jours si l'utilisateur a ignore — Apple ITP impose
// un rappel periodique pour que l'auth perenne reste effective.
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface NavigatorWithStandalone {
  standalone?: boolean;
}

export function isRunningAsStandaloneApp(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if ((window.navigator as Navigator & NavigatorWithStandalone).standalone) {
      return true;
    }
    if (window.matchMedia?.("(display-mode: standalone)").matches) {
      return true;
    }
  } catch {
    /* swallow */
  }
  return false;
}

export function isIpadSafari(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  // iPadOS 13+ se presente comme Mac dans le UA — il faut detecter via
  // `maxTouchPoints` pour distinguer du vrai Mac.
  const isIpad =
    /iPad/.test(ua) ||
    (window.navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
  const isSafari =
    /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIpad && isSafari;
}

export function shouldShowInstallBanner(): boolean {
  if (typeof window === "undefined") return false;
  if (isRunningAsStandaloneApp()) return false;
  if (!isIpadSafari()) return false;
  try {
    const v = window.localStorage.getItem(DISMISS_KEY);
    if (v) {
      const dismissedAt = Number.parseInt(v, 10);
      if (
        Number.isFinite(dismissedAt) &&
        Date.now() - dismissedAt < DISMISS_TTL_MS
      ) {
        return false;
      }
    }
  } catch {
    /* swallow */
  }
  return true;
}

export function dismissInstallBanner(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* swallow */
  }
}
